import React from 'react';
import { useSignup } from './SignupProvider';

const SuccessStep: React.FC = () => {
  const { state, startAdditionalRestaurant } = useSignup();
  const { restaurant, manager } = state.data;

  const handleCreateAnotherRestaurant = () => {
    if (manager) {
      startAdditionalRestaurant(manager);
    }
  };

  const handleGoToLogin = () => {
    // In production, this would redirect to the MAC console login
    window.open('https://console.tablechat.io', '_blank');
  };

  return (
    <div className="success-step">
      <div className="success-content">
        <div className="success-icon">
          <div className="checkmark">✓</div>
        </div>

        <div className="success-header">
          <h1>Welcome to TableChat&trade;!</h1>
          <p>Your restaurant account has been created successfully</p>
        </div>

        <div className="account-details">
          <div className="detail-card">
            <h3>Restaurant Account Created</h3>
            <div className="detail-item">
              <span className="label">Restaurant Name:</span>
              <span className="value">{restaurant?.name}</span>
            </div>
            <div className="detail-item">
              <span className="label">Location:</span>
              <span className="value">{restaurant?.city}, {restaurant?.state}</span>
            </div>
            <div className="detail-item">
              <span className="label">Tables:</span>
              <span className="value">{state.data.tableCount}</span>
            </div>
            <div className="detail-item">
              <span className="label">NFC Tags Ordered:</span>
              <span className="value">{state.data.nfcCount}</span>
            </div>
          </div>

          <div className="detail-card">
            <h3>Manager Account</h3>
            <div className="detail-item">
              <span className="label">Manager Name:</span>
              <span className="value">{manager?.displayName}</span>
            </div>
            <div className="detail-item">
              <span className="label">Email:</span>
              <span className="value">{manager?.email}</span>
            </div>
            <div className="detail-item">
              <span className="label">Role:</span>
              <span className="value">Manager Admin</span>
            </div>
          </div>
        </div>

        <div className="next-steps">
          <h3>What's Next?</h3>
          <div className="steps-grid">
            <div className="step-item">
              <div className="step-icon">📧</div>
              <div className="step-content">
                <h4>Check Your Email</h4>
                <p>Your receipt from Stripe will be sent shortly.</p>
              </div>
            </div>

            <div className="step-item">
              <div className="step-icon">📱</div>
              <div className="step-content">
                <h4>Access Manager Console</h4>
                <p>Use the link below to log into your restaurant management dashboard</p>
              </div>
            </div>

            <div className="step-item">
              <div className="step-icon">📦</div>
              <div className="step-content">
                <h4>NFC Tags Shipping</h4>
                <p>Your {state.data.nfcCount} NFC table taps will ship within 2-3 business days</p>
              </div>
            </div>

            <div className="step-item">
              <div className="step-icon">🎯</div>
              <div className="step-content">
                <h4>Setup Support</h4>
                <p>Please contact our team immediately if you have any questions for setup</p>
              </div>
            </div>
          </div>
        </div>

        <div className="success-actions">
          <button
            type="button"
            className="btn btn-primary btn-large"
            onClick={handleGoToLogin}
          >
            Access Manager Console
          </button>
          
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCreateAnotherRestaurant}
          >
            Create Another Restaurant
          </button>
        </div>

        <div className="support-info">
          <div className="support-content">
            <h4>Need Help?</h4>
            <p>Our support team is here to help you get started:</p>
            <div className="support-contacts">
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <a href="mailto:support@tablechat.io">support@tablechat.io</a>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <span>404.226.2614</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .success-step {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          text-align: center;
        }

        .success-content {
          display: flex;
          flex-direction: column;
          gap: 32px;
        }

        .success-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }

        .checkmark {
          width: 80px;
          height: 80px;
          background: linear-gradient(45deg, #28a745, #20c997);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          font-weight: bold;
          animation: checkmarkPop 0.6s ease-out;
        }

        @keyframes checkmarkPop {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .success-header h1 {
          color: #28a745;
          margin-bottom: 12px;
          font-size: 2.5rem;
          font-weight: 700;
        }

        .success-header p {
          color: #6c757d;
          font-size: 1.2rem;
          margin: 0;
        }

        .account-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          text-align: left;
        }

        .detail-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 24px;
          border-left: 4px solid #2196F3;
        }

        .detail-card h3 {
          margin: 0 0 16px 0;
          color: #2196F3;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .detail-item {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
        }

        .detail-item:last-child {
          border-bottom: none;
        }

        .detail-item .label {
          color: #6c757d;
          font-weight: 500;
        }

        .detail-item .value {
          color: #495057;
          font-weight: 600;
        }

        .next-steps {
          text-align: center;
        }

        .next-steps h3 {
          color: #495057;
          margin-bottom: 24px;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          text-align: left;
        }

        .step-item {
          display: flex;
          gap: 16px;
          background: white;
          border-radius: 12px;
          padding: 20px;
          border: 2px solid #e9ecef;
          transition: all 0.2s;
        }

        .step-item:hover {
          border-color: #2196F3;
          transform: translateY(-2px);
        }

        .step-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .step-content h4 {
          margin: 0 0 8px 0;
          color: #2196F3;
          font-size: 1rem;
          font-weight: 600;
        }

        .step-content p {
          margin: 0;
          color: #6c757d;
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .success-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-top: 16px;
        }

        .btn {
          padding: 14px 28px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary {
          background: #2196F3;
          color: white;
        }

        .btn-primary:hover {
          background: #1976D2;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background: #5a6268;
          transform: translateY(-1px);
        }

        .btn-large {
          font-size: 1.1rem;
          padding: 16px 32px;
          font-weight: 600;
        }

        .support-info {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 24px;
          border: 1px solid #e9ecef;
        }

        .support-content h4 {
          margin: 0 0 12px 0;
          color: #495057;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .support-content p {
          margin: 0 0 16px 0;
          color: #6c757d;
        }

        .support-contacts {
          display: flex;
          justify-content: center;
          gap: 32px;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #2196F3;
          font-weight: 500;
        }

        .contact-item a {
          color: #2196F3;
          text-decoration: none;
        }

        .contact-item a:hover {
          text-decoration: underline;
        }

        .contact-icon {
          font-size: 1.2rem;
        }

        @media (max-width: 768px) {
          .account-details {
            grid-template-columns: 1fr;
          }

          .steps-grid {
            grid-template-columns: 1fr;
          }

          .success-actions {
            flex-direction: column;
          }

          .support-contacts {
            flex-direction: column;
            gap: 16px;
          }

          .success-step {
            padding: 16px;
          }

          .success-header h1 {
            font-size: 2rem;
          }

          .success-header p {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default SuccessStep;
